/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import firebase from 'firebase/compat/app';
import {firebaseAuth} from '@/api/firebase';
import User from '@/api/auth/user';
// noinspection TypeScriptCheckImport
import {UserId} from 'spine-web/proto/spine/core/user_id_pb';

/**
 * Subscription options with callbacks to react on updates in authentication state.
 */
interface AuthSubscribeOptions {
  /**
   * Called when a user is signed in to the app.
   *
   * @param {User} user info of the currently authenticated License Server user
   */
  onSignIn: (user: User) => void,

  /**
   * Called when a user is signed out from the app.
   */
  onSignOut: () => void,

  /**
   * Called when a user sign-in is failed.
   */
  onSignInFailed: () => void
}

/**
 * Function that cancels a subscription on authentication state updates.
 */
type Unsubscribe = firebase.Unsubscribe;

/**
 * Service that performs user authentication with Firebase Auth for the License Server web app.
 */
class FirebaseAuth {
  /**
   * The time gap in milliseconds before `firebase.auth.IdTokenResult#expirationTime`
   * when the token starts to be considered as required for the refresh.
   *
   * <p>This gap is considered sufficient for delivering non-expired tokens to the backend.
   *
   * <p>The standard Firebase ID token lifetime is 60 minutes.
   */
  private static readonly idTokenExpirationGap = 60 * 1000;

  private firebaseUser: firebase.User = null;
  private idTokenResult: firebase.auth.IdTokenResult = null;

  /**
   * Creates a new service instance.
   *
   * @param {firebase.auth.Auth} auth
   *         instance of the Firebase Auth service for this app
   * @param {firebase.auth.Auth.Persistence} [persistence=NONE]
   *         the level of Firebase user session persistence.
   *         Defaults to `firebase.auth.Auth.Persistence.LOCAL` which is a maximum level.
   */
  constructor(
      private readonly auth: firebase.auth.Auth,
      private readonly persistence: firebase.auth.Auth.Persistence = firebase.auth.Auth.Persistence.LOCAL
  ) {
    this.setPersistence(persistence);
  }

  /**
   * Subscribes to updates of the Firebase Auth state.
   *
   * <p>Emits the following auth state updates:
   *  - when a user has signed in with Firebase Auth.
   *  - when a user has signed out with Firebase Auth.
   *  - when a user has attempted to sign in with Firebase Auth, but failed.
   *
   * @return {Unsubscribe} function that removes the subscription
   */
  public subscribe(options: AuthSubscribeOptions): Unsubscribe {
    return this.auth.onAuthStateChanged(async (user: firebase.User) => {
      if (user) {
        this.signedIn(options, user);
      } else {
        try {
          await this.auth.getRedirectResult();
        } catch (err) {
          if (FirebaseAuth.isRegistrationBlocked(err)) {
            options.onSignInFailed();
            return;
          }
        }
        options.onSignOut();
      }
    });
  }

  private async signedIn(options: AuthSubscribeOptions, firebaseUser: firebase.User) {
    this.firebaseUser = firebaseUser;
    const user = await this.toUserProfile(firebaseUser);
    options.onSignIn(user);
  }

  /**
   * Tells if the provided error represents a blocked user registration by the `beforeCreate`
   * Firebase function.
   */
  private static isRegistrationBlocked(error): boolean {
    const lsAuthErrors = [
        'ls-auth/user-not-registered',
        'ls-auth/signup-denied',
    ];
    return error.code === 'auth/internal-error'
        && error.message
        && lsAuthErrors.some(lsError => error.message.includes(lsError));
  }

  /**
   * Performs authentication using the Google provider.
   *
   * <p>Redirects the user to the authentication page from the current page.
   */
  public signIn() {
    const provider = FirebaseAuth.newGoogleAuthProvider();
    this.auth.signInWithRedirect(provider);
  }

  /**
   * Creates a new `GoogleAuthProvider` with a prompt to select a user account from the list.
   */
  private static newGoogleAuthProvider(): firebase.auth.GoogleAuthProvider {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    return provider;
  }

  /**
   * Returns the Firebase Auth ID token JWT string of the currently authenticated
   * Firebase user, if any.
   *
   * @returns {Promise<string | null>}
   */
  public async getFirebaseToken(): Promise<string | null> {
    const idTokenResult = await this.getIdTokenResult();
    return idTokenResult?.token ?? null;
  }

  /**
   * Sets the session persistence level.
   *
   * @param {firebase.auth.Auth.Persistence} value the auth persistence level
   */
  private setPersistence(value) {
    this.auth.setPersistence(value);
  }

  /**
   * Creates a {@link User} profile from the provided Firebase user.
   */
  private async toUserProfile(user: firebase.User): Promise<User> {
    const idTokenResult = await this.getIdTokenResult();
    const systemUserId = FirebaseAuth.getSystemUserId(idTokenResult);
    return {
      id: systemUserId,
      displayName: user.displayName,
      email: user.email,
      avatarUrl: user.photoURL,
    };
  }

  /**
   * Returns the `firebase.auth.IdTokenResult` of the currently authenticated Firebase user, if any.
   *
   * <p>Tracks the expiration time of the Firebase ID token, refreshes it if necessary.
   *
   * @returns {Promise<firebase.auth.IdTokenResult | null>}
   */
  private async getIdTokenResult(): Promise<firebase.auth.IdTokenResult | null> {
    if (!this.firebaseUser) {
      return null;
    }
    if (FirebaseAuth.requiresRefresh(this.idTokenResult)) {
      this.idTokenResult = await this.firebaseUser.getIdTokenResult(true /* force refresh */);
    }
    return this.idTokenResult;
  }

  /**
   * Retrieves system ID of a user from the given ID token. System user ID is an ID
   * of License Server user that is assigned to them during creation.
   *
   * <p>The system ID is obtained from custom claims of the ID token
   * and assigned to Firebase user before first sign-in on the backend.
   *
   * @param {firebase.auth.IdTokenResult} idTokenResult the ID token result to retrieve user
   *                                                    system ID from
   * @returns {UserId} obtained system user ID
   * @throws {Error} if custom claims doesn't have system user ID due to the backend error
   */
  private static getSystemUserId(idTokenResult): UserId {
    const claims = idTokenResult.claims || {};
    if (!claims.systemUserId) {
      throw new Error('Authenticated user doesn\'t have "systemUserId".');
    }
    return new UserId()
        .setValue(claims.systemUserId);
  }

  /**
   * Indicates that the given `firebase.auth.IdTokenResult` requires refresh.
   *
   * <p>The ID token result requires to be refreshed if the time left before its expiration is less
   * than `idTokenExpirationGap`.
   *
   * <p>Note, that `IdTokenResult#expirationTime` contains expiration time formatted as a
   * UTC string. To calculate the duration after which expiration will come, we use `Date.now()`
   * that returns current milliseconds in UTC and the UTC milliseconds of the expiration time.
   *
   * @param {firebase.auth.IdTokenResult} idTokenResult the token result to be checked
   * @returns {boolean}
   */
  private static requiresRefresh(idTokenResult): boolean {
    if (!idTokenResult || !idTokenResult.expirationTime) {
      return true;
    }
    const expirationTime = new Date(idTokenResult.expirationTime);
    const now = Date.now();
    const expiresAfter = expirationTime.getTime() - now;
    return expiresAfter < FirebaseAuth.idTokenExpirationGap;
  }
}

/**
 * Firebase authentication service for the current web app instance.
 */
let _auth: FirebaseAuth = null;

/**
 * Returns the Firebase authentication service.
 *
 * <p>If the service is not initialized, creates a new instance
 * of the Firebase authentication service.
 *
 * @return {FirebaseAuth}
 */
export default function auth(): FirebaseAuth {
  if (!_auth) {
    _auth = new FirebaseAuth(firebaseAuth);
  }
  return _auth;
}
